import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying, success, error, already_verified
  const [message, setMessage] = useState("");
  
  const token = searchParams.get("token");

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus("error");
      setMessage("No verification token provided");
    }
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, { token });
      if (response.data.already_verified) {
        setStatus("already_verified");
        setMessage("Your email has already been verified.");
      } else {
        setStatus("success");
        setMessage("Your email has been verified successfully!");
      }
    } catch (error) {
      setStatus("error");
      // Fallback only — the API normally supplies the detail. Avoid claiming the
      // link "expired": verification tokens never expire, they get replaced when
      // a newer verification email is requested.
      setMessage(
        error.response?.data?.detail ||
        "Verification failed. If more than one verification email was sent, only the most recent link works — try that one, or request a new link."
      );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-24 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center"
      >
        {status === "verifying" && (
          <>
            <Loader2 className="w-16 h-16 text-purple animate-spin mx-auto mb-6" />
            <h1 className="font-syne text-2xl font-bold text-white mb-4">
              Verifying Your Email
            </h1>
            <p className="text-slate-400">Please wait...</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="font-syne text-2xl font-bold text-white mb-4">
              Email Verified!
            </h1>
            <p className="text-slate-400 mb-8">{message}</p>
            <p className="text-slate-400 mb-8">
              You now have full access to all community features.
            </p>
            <Link to="/">
              <Button className="bg-purple hover:bg-purple-hover text-white">
                Go to Homepage
              </Button>
            </Link>
          </>
        )}

        {status === "already_verified" && (
          <>
            <CheckCircle className="w-16 h-16 text-purple mx-auto mb-6" />
            <h1 className="font-syne text-2xl font-bold text-white mb-4">
              Already Verified
            </h1>
            <p className="text-slate-400 mb-8">{message}</p>
            <Link to="/">
              <Button className="bg-purple hover:bg-purple-hover text-white">
                Go to Homepage
              </Button>
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-syne text-2xl font-bold text-white mb-4">
              Verification Failed
            </h1>
            <p className="text-slate-400 mb-8">{message}</p>
            <div className="space-y-4">
              <Link to="/login">
                <Button className="bg-purple hover:bg-purple-hover text-white w-full">
                  Go to Login
                </Button>
              </Link>
              <p className="text-sm text-slate-500">
                Need a new verification link? Log in and request a new one from your profile.
              </p>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
